// ============================================================
// InwardTab.jsx  —  Noble Enterprises (tape module)
//
// WHAT'S ON THIS SCREEN
//   • Receipt list — click a row to see the rolls that arrived
//   • New receipt form:
//       pick a PO → add the lines that arrived → enter a roll
//       number for each physical roll
//
// THE "METERS VARY" CHECKBOX
// Each roll defaults to the jumbo's spec meters. Tick the box
// on a roll and a field appears, pre-filled with the spec, so
// only the roll that differs needs touching.
//
// HOW THIS FILE IS ORGANISED
//   A. Shared UI bits
//   B. RollRows      — the roll-number rows for one item
//   C. ItemBlock     — one PO line being received
//   D. InwardForm
//   E. InwardDetailRow
//   F. InwardTab
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Trash2, X, Check, FileText, ChevronDown as Chevron } from 'lucide-react';
import PrintButton from '../print/PrintButton';
import { hasFullAccess } from '../../../utils/access';
import { inwardPrintConfig } from './reports/inwardPrint.config';
import InwardReportModal from './reports/InwardReportModal';
import WindowModal from '../components/WindowModal';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const C = {
  brand:      '#f39c12',
  text:       '#1a2332',
  textSoft:   '#64748b',
  textMuted:  '#94a3b8',
  border:     '#e2e8f0',
  borderSoft: '#f1f5f9',
  card:       '#ffffff',
  rowAlt:     '#fafbfc',
  danger:     '#dc2626',
  dangerBg:   '#fee2e2',
  success:    '#059669',
  successBg:  '#d1fae5',
  warning:    '#d97706',
  info:       '#0284c7',
  infoBg:     '#e0f2fe',
};

const money = (n) => `Rs ${Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 })}`;
const today = () => new Date().toISOString().split('T')[0];
const fmtDate = (d) => new Date(d).toLocaleDateString('en-GB');
// Received rolls are stored in yards (converted at receipt).
// The entry form still collects meters — that's the PO unit.
const rollYards = (n) => `${Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 })} yards`;

const meters = (n) => `${Number(n || 0).toLocaleString('en-PK', { maximumFractionDigits: 0 })} m`;

// The PO line states its own spec. Any part can be null on an
// incomplete line, so each piece is guarded and the join skips
// the blanks rather than printing "· µ ·".
const specLabel = (it) => {
  if (!it) return '—';
  const parts = [
    it.tape_type_name,
    it.micron != null ? `${Number(it.micron)}µ` : null,
    it.width_mm != null ? `${Number(it.width_mm)}mm` : null,
    it.length_per_roll_m != null ? `${Number(it.length_per_roll_m)} m/roll` : null,
    it.weight_per_roll_kg != null ? `${Number(it.weight_per_roll_kg)} kg` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
};


// ============================================================
// A. SHARED UI BITS
// ============================================================

const inputStyle = {
  width: '100%', padding: '10px 13px',
  border: `1px solid ${C.border}`, borderRadius: '8px',
  fontSize: '14px', color: C.text, boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#ffffff',
};

const Field = ({ label, children }) => (
  <div>
    <label style={{
      display: 'block', fontSize: '12px', fontWeight: '600',
      color: C.textSoft, marginBottom: '6px',
    }}>
      {label}
    </label>
    {children}
  </div>
);

const TextInput = (props) => <input style={inputStyle} {...props} />;

const SectionHead = ({ children }) => (
  <div style={{
    fontSize: '12px', fontWeight: '700', color: C.textSoft,
    textTransform: 'uppercase', letterSpacing: '0.4px',
    paddingBottom: '8px', marginBottom: '16px',
    borderBottom: `1px solid ${C.borderSoft}`,
  }}>
    {children}
  </div>
);

const Btn = ({ variant = 'primary', children, ...props }) => {
  const styles = {
    primary: { background: C.brand,   color: '#ffffff',  border: 'none' },
    ghost:   { background: '#ffffff', color: C.textSoft, border: `1px solid ${C.border}` },
  };
  return (
    <button
      style={{
        ...styles[variant],
        padding: '10px 18px', borderRadius: '8px', fontSize: '14px',
        fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
        display: 'inline-flex', alignItems: 'center', gap: '7px',
      }}
      {...props}
    >
      {children}
    </button>
  );
};

const IconBtn = ({ danger, title, children, ...props }) => (
  <button
    title={title}
    style={{
      width: '32px', height: '32px', borderRadius: '7px',
      border: `1px solid ${C.border}`, background: '#ffffff',
      color: danger ? C.danger : C.textSoft, cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}
    {...props}
  >
    {children}
  </button>
);

const Banner = ({ type, message, onClose }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
         <div style={{
      background: isError ? C.dangerBg : C.successBg,
      color:      isError ? C.danger   : C.success,
      padding: '16px 20px', borderRadius: '10px', marginBottom: '16px',
      fontSize: '16px', fontWeight: '600',
      display: 'flex', alignItems: 'center', gap: '12px',
      border: `1px solid ${isError ? C.danger : C.success}`,
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}>
        <X size={16} />
      </button>
    </div>
  );
};

// Small checkbox with a label
const Check2 = ({ checked, onChange, label }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: '7px',
      background: 'none', border: 'none', cursor: 'pointer',
      padding: 0, fontFamily: 'inherit', whiteSpace: 'nowrap',
    }}
  >
    <span style={{
      width: '17px', height: '17px', borderRadius: '4px', flexShrink: 0,
      border: `1.5px solid ${checked ? C.brand : C.border}`,
      background: checked ? C.brand : '#ffffff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {checked && <Check size={12} color="#ffffff" strokeWidth={3} />}
    </span>
    <span style={{ fontSize: '12px', color: checked ? C.text : C.textSoft }}>{label}</span>
  </button>
);

const thStyle = {
  textAlign: 'left', padding: '11px 22px', fontSize: '12px',
  fontWeight: '600', color: C.textSoft, background: C.rowAlt,
  textTransform: 'uppercase', letterSpacing: '0.3px',
};
const tdStyle = {
  padding: '13px 22px', fontSize: '14px', color: C.text,
  borderTop: `1px solid ${C.borderSoft}`,
};


// ============================================================
// B. ROLL ROWS
//
// One row per physical roll. Roll number is required.
// Meters default to the spec and only become editable when
// that roll's "meters vary" box is ticked.
// ============================================================

const RollRows = ({ rolls, setRolls, specMeters }) => {
  const updateRoll = (index, patch) =>
    setRolls(rolls.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {rolls.map((roll, index) => (
        <div
          key={index}
          style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            flexWrap: 'wrap',
            padding: '10px 12px', borderRadius: '8px',
            background: '#ffffff', border: `1px solid ${C.borderSoft}`,
          }}
        >
          <span style={{
            fontSize: '12px', fontWeight: '600', color: C.textMuted,
            width: '52px', flexShrink: 0,
          }}>
            Roll {index + 1}
          </span>

          <div style={{ flex: '1 1 160px', minWidth: '140px' }}>
            <TextInput
              value={roll.vendor_roll_number}
              onChange={e => updateRoll(index, { vendor_roll_number: e.target.value })}
              placeholder="Roll number"
            />
          </div>

          {/* Spec meters, shown until the box is ticked */}
          {!roll.varies && (
            <span style={{
              fontSize: '13px', color: C.textSoft,
              width: '90px', flexShrink: 0,
            }}>
              {meters(specMeters)}
            </span>
          )}

          {/* Editable meters once ticked */}
          {roll.varies && (
            <div style={{ width: '120px', flexShrink: 0 }}>
              <TextInput
                type="number" step="0.01"
                value={roll.total_meters}
                onChange={e => updateRoll(index, { total_meters: e.target.value })}
                placeholder={String(specMeters)}
              />
            </div>
          )}

          <Check2
            checked={roll.varies}
            label="meters vary"
            onChange={(v) => updateRoll(index, {
              varies: v,
              // Ticking pre-fills with the spec so only the
              // difference needs typing. Unticking clears it,
              // which tells the backend to use the spec.
              total_meters: v ? String(specMeters) : '',
            })}
          />
        </div>
      ))}
    </div>
  );
};


// ============================================================
// C. ITEM BLOCK
//
// One PO line being received. Changing the quantity resizes
// the roll list — grow keeps what's typed, shrink drops the tail.
// ============================================================

const ItemBlock = ({ item, poItem, onChange, onRemove }) => {
   const pending = Number(poItem.pending_quantity);
  const spec    = Number(poItem.length_per_roll_m);

  const setQuantity = (raw) => {
    const qty = Math.max(0, Math.min(Number(raw) || 0, pending));

    const rolls = [...item.rolls];
    while (rolls.length < qty) rolls.push({ vendor_roll_number: '', total_meters: '', varies: false });
    rolls.length = qty;

    onChange({ ...item, quantity: raw === '' ? '' : qty, rolls });
  };

  const lineTotal = Number(item.quantity || 0) * Number(poItem.rate);

  return (
    <div style={{
      border: `1px solid ${C.border}`, borderRadius: '10px',
      overflow: 'hidden', marginBottom: '14px',
    }}>
      {/* Header */}
      <div style={{
        padding: '14px 16px', background: C.rowAlt,
        display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
      }}>
               <div style={{ flex: 1, minWidth: '200px' }}>
          <div style={{ fontWeight: '700', fontSize: '14px', color: C.text }}>
            {specLabel(poItem)}
          </div>
          <div style={{ fontSize: '12px', color: C.textMuted, marginTop: '2px' }}>
            Ordered {poItem.quantity} · Pending {pending} · {money(poItem.rate)} per roll
          </div>
        </div>

        <div style={{ width: '130px' }}>
          <Field label={`Receiving (max ${pending})`}>
            <TextInput
              type="number"
              min="0"
              max={pending}
              value={item.quantity}
              onChange={e => setQuantity(e.target.value)}
              placeholder="0"
            />
          </Field>
        </div>

        <IconBtn danger title="Remove item" onClick={onRemove}>
          <Trash2 size={15} />
        </IconBtn>
      </div>

      {/* Rolls */}
      {item.rolls.length > 0 && (
        <div style={{ padding: '14px 16px', background: C.borderSoft }}>
          <RollRows
            rolls={item.rolls}
            setRolls={rolls => onChange({ ...item, rolls })}
            specMeters={spec}
          />
          <div style={{
            marginTop: '12px', display: 'flex', justifyContent: 'flex-end',
            alignItems: 'baseline', gap: '10px',
          }}>
            <span style={{ fontSize: '12px', color: C.textSoft, fontWeight: '600' }}>Line total</span>
            <span style={{ fontSize: '16px', fontWeight: '700', color: C.text }}>
              {money(lineTotal)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};


// ============================================================
// D. INWARD FORM
// ============================================================

const InwardForm = ({
  form, setForm, openPos, poDetail, onPoChange, onSave, onCancel, saving,
  selectedVendor, setSelectedVendor, banner, onClearBanner,
}) => {
  const vendors = [...new Map(openPos.map(po => [po.vendor_name, po.vendor_name])).values()];
  const posForVendor = openPos.filter(po => po.vendor_name === selectedVendor);
  // PO lines that still have something outstanding and aren't
  // already added to this receipt
  const availableLines = (poDetail?.items || []).filter(
    pi => Number(pi.pending_quantity) > 0
       && !form.items.some(it => String(it.po_item_id) === String(pi.id))
  );

  const addItem = (poItemId) => {
    if (!poItemId) return;
    setForm({
      ...form,
      items: [...form.items, { po_item_id: poItemId, quantity: '', rolls: [] }],
    });
  };

  const updateItem = (index, next) =>
    setForm({ ...form, items: form.items.map((it, i) => (i === index ? next : it)) });

  const removeItem = (index) =>
    setForm({ ...form, items: form.items.filter((_, i) => i !== index) });

  const billTotal = form.items.reduce((sum, it) => {
    const poItem = poDetail?.items.find(pi => String(pi.id) === String(it.po_item_id));
    return sum + (Number(it.quantity || 0) * Number(poItem?.rate || 0));
  }, 0);

  const num = (v) => (v === '' || v == null ? 0 : Number(v) || 0);
  const totalCharges = num(form.freight_charges)
                     + num(form.labour_charges)
                     + num(form.other_charges);

  // GST is not claimable — it is a real cost and lands on the rolls.
  // Only this receipt's share, prorated by the value arriving.
  // There is no subtotal column, so it is total less GST.
  const poSubtotal = Number(poDetail?.total_amount || 0)
                   - Number(poDetail?.gst_amount || 0);
  const gstTotal = poSubtotal > 0
    ? (Number(poDetail?.gst_amount || 0) * billTotal) / poSubtotal
    : 0;

  const landedTotal = billTotal + gstTotal + totalCharges;

  // Soft hint only — the terms say who was meant to pay freight.
  const terms = poDetail?.delivery_terms;
  const freightIn = num(form.freight_charges);
  const termsWarning =
    terms === 'delivered' && freightIn > 0
      ? 'This order is delivered — the vendor was to pay freight.'
      : terms === 'ex_works' && freightIn === 0 && billTotal > 0
      ? 'This order is ex-works — freight is normally expected.'
      : '';
  const totalRolls = form.items.reduce((sum, it) => sum + it.rolls.length, 0);

  // Meters actually being received, so the per-meter figure below
  // reflects this receipt rather than the jumbo spec.
    const totalMetersIn = form.items.reduce((sum, it) => {
    const poItem = poDetail?.items.find(pi => String(pi.id) === String(it.po_item_id));
    const spec = Number(poItem?.length_per_roll_m || 0);
    return sum + it.rolls.reduce(
      (s, r) => s + (r.varies && r.total_meters !== '' ? Number(r.total_meters) || 0 : spec), 0
    );
  }, 0);

  const avgCostPerMeter = totalMetersIn > 0 ? landedTotal / totalMetersIn : 0;

   return (
    <div>

      <SectionHead>Vendor &amp; terms</SectionHead>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px', marginBottom: '22px',
      }}>
        <Field label="Vendor">
          <select
            style={inputStyle}
            value={selectedVendor}
            onChange={e => {
              setSelectedVendor(e.target.value);
              onPoChange(''); // reset PO when vendor changes
            }}
          >
            <option value="">Select vendor…</option>
            {vendors.map(v => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </Field>

        <Field label="Purchase order">
          <select
            style={inputStyle}
            value={form.po_id}
            disabled={!selectedVendor}
            onChange={e => onPoChange(e.target.value)}
          >
            <option value="">Select PO…</option>
            {posForVendor.map(po => (
              <option key={po.id} value={po.id}>{po.po_number}</option>
            ))}
          </select>
          {poDetail?.delivery_terms && (
            <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '5px' }}>
              {poDetail.delivery_terms === 'delivered'
                ? 'Delivered — vendor pays freight'
                : 'Ex-works — we pay freight'}
            </div>
          )}
        </Field>

        <Field label="Bill number">
          <TextInput
            value={form.bill_number}
            onChange={e => setForm({ ...form, bill_number: e.target.value })}
            placeholder="Vendor's bill number"
          />
        </Field>

        <Field label="Received date">
          <TextInput
            type="date"
            value={form.received_date}
            onChange={e => setForm({ ...form, received_date: e.target.value })}
          />
        </Field>

        <Field label="Notes">
          <TextInput
            value={form.notes}
            onChange={e => setForm({ ...form, notes: e.target.value })}
            placeholder="Optional"
          />
        </Field>
      </div>

            {/* Items — only meaningful once a PO is picked */}
      {form.po_id && poDetail && (
        <>
          <SectionHead>Line items</SectionHead>

          {form.items.map((item, index) => {
            const poItem = poDetail.items.find(pi => String(pi.id) === String(item.po_item_id));
            if (!poItem) return null;
            return (
              <ItemBlock
                key={item.po_item_id}
                item={item}
                poItem={poItem}
                onChange={next => updateItem(index, next)}
                onRemove={() => removeItem(index)}
              />
            );
          })}

          {availableLines.length > 0 ? (
            <select
              style={{ ...inputStyle, maxWidth: '400px' }}
              value=""
              onChange={e => addItem(e.target.value)}
            >
              <option value="">+ Add item that arrived…</option>
                            {availableLines.map(pi => (
                <option key={pi.id} value={pi.id}>
                  {specLabel(pi)} — {pi.pending_quantity} pending
                </option>
              ))}
            </select>
          ) : (
            form.items.length === 0 && (
              <p style={{ fontSize: '13px', color: C.textMuted, margin: 0 }}>
                Nothing left to receive on this order.
              </p>
            )
          )}

             {billTotal > 0 && (
            <>
              <SectionHead>Totals</SectionHead>
              {/* Charges paid on the receipt, not on the order.
                  These load onto the rolls, so cost of goods reflects
                  what the material cost to get onto the floor — not
                  just what the vendor invoiced. */}
              <div style={{
                marginTop: '18px', padding: '16px 18px', borderRadius: '10px',
                background: '#ffffff', border: `1px solid ${C.border}`,
              }}>
                <div style={{
                  fontSize: '12px', fontWeight: '600', color: C.textSoft,
                  textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '12px',
                }}>
                  Charges on this receipt
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '14px',
                }}>
                  <Field label="Freight / builty">
                    <TextInput
                      type="number" step="0.01" min="0"
                      value={form.freight_charges}
                      onChange={e => setForm({ ...form, freight_charges: e.target.value })}
                      placeholder="0"
                    />
                  </Field>
                  <Field label="Labour">
                    <TextInput
                      type="number" step="0.01" min="0"
                      value={form.labour_charges}
                      onChange={e => setForm({ ...form, labour_charges: e.target.value })}
                      placeholder="0"
                    />
                  </Field>
                  <Field label="Other">
                    <TextInput
                      type="number" step="0.01" min="0"
                      value={form.other_charges}
                      onChange={e => setForm({ ...form, other_charges: e.target.value })}
                      placeholder="0"
                    />
                  </Field>
                </div>

                {totalCharges > 0 && (
                  <p style={{ margin: '11px 0 0', fontSize: '12px', color: C.textMuted }}>
                    Spread across {totalRolls} roll{totalRolls === 1 ? '' : 's'} by value.
                    A dearer roll carries a bigger share.
                  </p>
                )}

                {termsWarning && (
                  <p style={{ margin: '9px 0 0', fontSize: '12px', color: C.warning }}>
                    {termsWarning}
                  </p>
                )}
              </div>

              <div style={{
                marginTop: '14px', padding: '16px 18px', borderRadius: '10px',
                background: '#ffffff', border: `1px solid ${C.border}`,
              }}>
                <div style={{
                  display: 'flex', alignItems: 'baseline', gap: '12px',
                  flexWrap: 'wrap', marginBottom: totalCharges > 0 ? '10px' : 0,
                }}>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: C.textSoft }}>
                    Goods
                  </span>
                  <span style={{ fontSize: '16px', fontWeight: '600', color: C.text }}>
                    {money(billTotal)}
                  </span>
                  {gstTotal > 0 && (
                    <>
                      <span style={{ fontSize: '13px', color: C.textMuted }}>+ GST</span>
                      <span style={{ fontSize: '16px', fontWeight: '600', color: C.text }}>
                        {money(gstTotal)}
                      </span>
                    </>
                  )}
                  {totalCharges > 0 && (
                    <>
                      <span style={{ fontSize: '13px', color: C.textMuted }}>+ charges</span>
                      <span style={{ fontSize: '16px', fontWeight: '600', color: C.text }}>
                        {money(totalCharges)}
                      </span>
                    </>
                  )}
                </div>

                <div style={{
                  display: 'flex', alignItems: 'baseline', gap: '12px',
                  flexWrap: 'wrap',
                  paddingTop: totalCharges > 0 ? '10px' : 0,
                  borderTop: totalCharges > 0 ? `1px solid ${C.borderSoft}` : 'none',
                }}>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: C.textSoft }}>
                    Landed total
                  </span>
                  <span style={{ fontSize: '22px', fontWeight: '700', color: C.text }}>
                    {money(landedTotal)}
                  </span>
                  {avgCostPerMeter > 0 && (
                    <span style={{ fontSize: '12px', color: C.textMuted }}>
                      ≈ Rs {Math.round(avgCostPerMeter).toLocaleString('en-PK')} per meter
                    </span>
                  )}
                </div>

                <p style={{ margin: '10px 0 0', fontSize: '12px', color: C.textMuted }}>
                  {money(billTotal + gstTotal)} posts to {poDetail.vendor_name}'s account
                  {gstTotal > 0 && ' (incl. GST)'}
                  {totalCharges > 0 && '; charges load onto the rolls'}
                </p>
              </div>
            </>
          )}
        </>
      )}
      <Banner
        type={banner.type}
        message={banner.message}
        onClose={onClearBanner}
      />

      <div style={{
        display: 'flex', gap: '10px', marginTop: '24px',
        paddingTop: '18px', borderTop: `1px solid ${C.borderSoft}`,
      }}>
        <Btn onClick={onSave} disabled={saving}>
          <Check size={16} /> {saving ? 'Saving…' : 'Receive goods'}
        </Btn>
        <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
      </div>
    </div>
  );
};


// ============================================================
// E. DETAIL ROW
// ============================================================

const InwardDetailRow = ({ inwardId, colSpan }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    axios.get(`${API}/tape/inward/${inwardId}`, { headers })
      .then(res => setData(res.data.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [inwardId]); // eslint-disable-line

  const cell = { padding: '9px 12px', fontSize: '13px', borderTop: `1px solid ${C.borderSoft}` };
  const head = {
    ...cell, background: '#ffffff', fontWeight: '600', color: C.textSoft,
    fontSize: '11px', textTransform: 'uppercase', borderTop: 'none',
  };

  return (
    <tr>
      <td colSpan={colSpan} style={{
        padding: 0, background: C.rowAlt, borderTop: `1px solid ${C.borderSoft}`,
      }}>
        <div style={{ padding: '18px 22px' }}>
          {loading && <p style={{ margin: 0, color: C.textSoft, fontSize: '13px' }}>Loading…</p>}

          {!loading && data && (
            <div style={{
              border: `1px solid ${C.border}`, borderRadius: '8px',
              overflow: 'hidden', background: '#ffffff',
            }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '520px' }}>
                  <thead>
                    <tr>
                      <th style={{ ...head, textAlign: 'left' }}>Roll number</th>
                      <th style={{ ...head, textAlign: 'left' }}>Jumbo</th>
                                            <th style={{ ...head, textAlign: 'right' }}>Yards</th>
                      <th style={{ ...head, textAlign: 'right' }}>Remaining</th>
                      <th style={{ ...head, textAlign: 'left' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rolls.map(roll => {
                      // A roll that arrived short of its spec is worth
                      // flagging — it feeds the yield expectation later.
                                            const isShort = Number(roll.total_yards) < Number(roll.spec_yards);
                      return (
                        <tr key={roll.id}>
                          <td style={{ ...cell, fontWeight: '600' }}>
                            {roll.vendor_roll_number || '—'}
                          </td>
                          <td style={cell}>{roll.jumbo_name}</td>
                          <td style={{ ...cell, textAlign: 'right' }}>
                                                        {rollYards(roll.total_yards)}
                            {isShort && (
                              <span style={{ fontSize: '11px', color: C.warning, marginLeft: '6px' }}>
                                short
                              </span>
                            )}
                          </td>
                          <td style={{ ...cell, textAlign: 'right', fontWeight: '600' }}>
                                                        {rollYards(roll.remaining_yards)}
                          </td>
                          <td style={{ ...cell, textTransform: 'capitalize', color: C.textSoft }}>
                            {roll.status}
                          </td>
                        </tr>
                      );
                    })}
                    {data.rolls.length === 0 && (
                      <tr>
                        <td colSpan="5" style={{ ...cell, textAlign: 'center', color: C.textMuted, padding: '24px' }}>
                          No rolls on this receipt
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!loading && data?.notes && (
            <div style={{ marginTop: '12px', fontSize: '13px', color: C.textSoft }}>
              <strong style={{ color: C.text }}>Notes:</strong> {data.notes}
            </div>
          )}

          {!loading && !data && (
            <p style={{ margin: 0, color: C.danger, fontSize: '13px' }}>Could not load this receipt.</p>
          )}
        </div>
      </td>
    </tr>
  );
};


// ============================================================
// F. MAIN COMPONENT
// ============================================================

const emptyForm = {
  po_id: '',
  bill_number: '',
  received_date: today(),
  notes: '',
  freight_charges: '',
  labour_charges: '',
  other_charges: '',
  items: [],
};

// The list query carries no rolls, so the print button fetches
// the full receipt on click and builds the config from it.
const fetchInwardConfig = async (inwardId) => {
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const res = await axios.get(`${API}/tape/inward/${inwardId}`, { headers });
  return inwardPrintConfig(res.data.data);
};

const InwardTab = () => {
  const [inwards, setInwards] = useState([]);
  const [openPos, setOpenPos] = useState([]);
  const [poDetail, setPoDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm]       = useState(null);
  const [saving, setSaving]   = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [banner, setBanner]   = useState({ type: '', message: '' });
  const [selectedVendor, setSelectedVendor] = useState('');
  const [showReport, setShowReport] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const notify = (type, message) => {
    setBanner({ type, message });
    if (type === 'success') setTimeout(() => setBanner({ type: '', message: '' }), 3000);
  };

  const load = useCallback(async () => {
    try {
      const [i, p] = await Promise.all([
        axios.get(`${API}/tape/inward`, { headers }),
        axios.get(`${API}/tape/purchase-orders/open`, { headers }),
      ]);
      setInwards(i.data.data || []);
      setOpenPos(p.data.data || []);
    } catch {
      notify('error', 'Could not load receipts. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // Picking a PO loads its lines and clears anything already added,
  // since those lines belong to the previous order.
  const handlePoChange = async (poId) => {
    setForm(f => ({ ...f, po_id: poId, items: [] }));
    if (!poId) { setPoDetail(null); return; }

    try {
      const res = await axios.get(`${API}/tape/purchase-orders/${poId}`, { headers });
      setPoDetail(res.data.data);
    } catch {
      setPoDetail(null);
      notify('error', 'Could not load that purchase order');
    }
  };

  const openForm = () => {
    setForm(emptyForm);
    setPoDetail(null);
    setSelectedVendor('');
  };

  const save = async () => {
    const f = form;
    if (!f.po_id)                   return notify('error', 'Pick a purchase order');
    if (!f.bill_number.trim())      return notify('error', 'Bill number is required');
    if (!f.received_date)           return notify('error', 'Received date is required');
    if (f.items.length === 0)       return notify('error', 'Add at least one item');

    for (const [label, val] of [
      ['Freight', f.freight_charges],
      ['Labour',  f.labour_charges],
      ['Other',   f.other_charges],
    ]) {
      if (val !== '' && (isNaN(Number(val)) || Number(val) < 0)) {
        return notify('error', `${label} charges must be 0 or more`);
      }
    }

    // Every roll needs a number before we bother the server
    for (const item of f.items) {
      const poItem = poDetail.items.find(pi => String(pi.id) === String(item.po_item_id));
      if (!(Number(item.quantity) > 0)) {
        return notify('error', `${poItem?.jumbo_name}: enter how many rolls arrived`);
      }
      for (const [index, roll] of item.rolls.entries()) {
        if (!roll.vendor_roll_number.trim()) {
          return notify('error', `${poItem?.jumbo_name}: roll ${index + 1} needs a roll number`);
        }
      }
    }

    setSaving(true);
    try {
      await axios.post(`${API}/tape/inward`, {
        po_id: f.po_id,
        bill_number: f.bill_number,
        received_date: f.received_date,
        notes: f.notes,
        freight_charges: f.freight_charges === '' ? 0 : f.freight_charges,
        labour_charges:  f.labour_charges  === '' ? 0 : f.labour_charges,
        other_charges:   f.other_charges   === '' ? 0 : f.other_charges,
        items: f.items.map(it => ({
          po_item_id: it.po_item_id,
          quantity: it.quantity,
          rolls: it.rolls.map(r => ({
            vendor_roll_number: r.vendor_roll_number,
            // Blank means "use the spec" — the backend fills it in
            total_meters: r.varies ? r.total_meters : '',
          })),
        })),
      }, { headers });

      notify('success', 'Goods received');
      setForm(null);
      setPoDetail(null);
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(
      `Delete receipt ${row.bill_number}? The rolls will be removed from stock and the order reopened.`
    )) return;

    try {
      await axios.delete(`${API}/tape/inward/${row.id}`, { headers });
      notify('success', 'Receipt deleted');
      if (expandedId === row.id) setExpandedId(null);
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not delete');
    }
  };

  // Headline numbers
  const totalRolls  = inwards.reduce((sum, i) => sum + Number(i.roll_count || 0), 0);
    const totalYards  = inwards.reduce((sum, i) => sum + Number(i.total_yards || 0), 0);
  const totalValue  = inwards.reduce((sum, i) => sum + Number(i.grand_total || 0), 0);

  return (
    <div>
      {showReport && (
        <InwardReportModal onClose={() => setShowReport(false)} />
      )}

      

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '14px', marginBottom: '20px',
      }}>
        {[
          ['Receipts', inwards.length],
          ['Rolls received', totalRolls],
                  ['Total received', rollYards(totalYards)],
          ['Purchase value', money(totalValue)],
        ].map(([label, value]) => (
          <div key={label} style={{
            background: C.card, border: `1px solid ${C.border}`,
            borderRadius: '10px', padding: '16px 18px',
          }}>
            <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
              {label}
            </div>
            <div style={{ fontSize: '22px', fontWeight: '700', color: C.text, marginTop: '4px' }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: '12px', overflow: 'hidden',
      }}>
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${C.borderSoft}`,
          display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: '180px' }}>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: C.text }}>
              Inward
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>
              Click a receipt to see the rolls that arrived.
            </p>
          </div>

         <Btn variant="ghost" onClick={() => setShowReport(true)}>
            <FileText size={16} /> Inward Report
          </Btn>

          {!form && (
            <Btn onClick={openForm} disabled={openPos.length === 0}>
              <Plus size={16} /> Receive goods
            </Btn>
          )}
        </div>

                {/* Messages for the list (delete, load errors) appear here,
            next to the table. The form shows its own inside the window. */}
        {!form && banner.message && (
          <div style={{ padding: '14px 22px 0' }}>
            <Banner
              type={banner.type}
              message={banner.message}
              onClose={() => setBanner({ type: '', message: '' })}
            />
          </div>
        )}

        {!form && openPos.length === 0 && !loading && (
          <div style={{
            padding: '14px 22px', background: C.infoBg,
            fontSize: '13px', color: C.info,
          }}>
            No open purchase orders. Raise one before receiving goods.
          </div>
        )}

                {form && (
          <WindowModal
            id="inward-form"
            title="Receive goods"
            subtitle={
              poDetail
                ? `${poDetail.po_number} · ${poDetail.vendor_name}`
                : 'New receipt'
            }
            width="820px"
            onClose={() => { setForm(null); setPoDetail(null); setSelectedVendor(''); }}
          >
                       <InwardForm
              form={form}
              setForm={setForm}
              openPos={openPos}
              poDetail={poDetail}
              onPoChange={handlePoChange}
              onSave={save}
              onCancel={() => { setForm(null); setPoDetail(null); setSelectedVendor(''); }}
              saving={saving}
              selectedVendor={selectedVendor}
              setSelectedVendor={setSelectedVendor}
              banner={banner}
              onClearBanner={() => setBanner({ type: '', message: '' })}
            />
          </WindowModal>
        )}

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Bill number</th>
                <th style={thStyle}>PO</th>
                <th style={thStyle}>Vendor</th>
                <th style={thStyle}>Date</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Rolls</th>
                                <th style={{ ...thStyle, textAlign: 'right' }}>Yards</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Value</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="8" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '36px' }}>
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && inwards.map(row => {
                const isOpen = expandedId === row.id;
                return (
                  <React.Fragment key={row.id}>
                    <tr
                      onClick={() => setExpandedId(isOpen ? null : row.id)}
                      style={{ cursor: 'pointer', background: isOpen ? C.rowAlt : 'transparent' }}
                    >
                      <td style={{ ...tdStyle, fontWeight: '700' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
                          <Chevron
                            size={15}
                            style={{
                              color: C.textMuted,
                              transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                              transition: 'transform 0.15s',
                            }}
                          />
                          {row.bill_number}
                        </span>
                      </td>
                      <td style={tdStyle}>{row.po_number}</td>
                      <td style={tdStyle}>{row.vendor_name}</td>
                      <td style={{ ...tdStyle, color: C.textSoft }}>{fmtDate(row.received_date)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600' }}>{row.roll_count}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: C.textSoft }}>
                                                {rollYards(row.total_yards)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600' }}>
                        {money(row.grand_total)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {/* stopPropagation, or clicking these would
                            also toggle the row open */}
                        <span
                          style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <PrintButton
                            variant="small"
                            label="Print"
                                                        filename={`Inward-Receipt-${row.bill_number}`}
                            getConfig={() => fetchInwardConfig(row.id)}
                          />
                          
                                                    {hasFullAccess('adhesive') && (
                            <IconBtn
                              danger
                              title="Delete receipt"
                              onClick={(e) => { e.stopPropagation(); remove(row); }}
                            >
                              <Trash2 size={15} />
                            </IconBtn>
                          )}
                        </span>
                      </td>
                    </tr>

                    {isOpen && <InwardDetailRow inwardId={row.id} colSpan={8} />}
                  </React.Fragment>
                );
              })}

              {!loading && inwards.length === 0 && (
                <tr>
                  <td colSpan="8" style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '40px' }}>
                    No goods received yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default InwardTab;